import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PageHero from "@/components/shared/PageHero";
import { TIMELINE_STEPS, MOCK_TRACK_RESULT } from "@/data/mock";
import { Search, CheckCircle2, Loader2 } from "lucide-react";

export default function TrackRequest() {
    const [id, setId] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const onSearch = (e) => {
        e.preventDefault();
        if (!id.trim()) return;
        setLoading(true);
        setResult(null);
        setTimeout(() => {
            setLoading(false);
            setResult({ ...MOCK_TRACK_RESULT, requestId: id.trim() });
        }, 900);
    };

    return (
        <>
            <PageHero
                testId="track-request-page"
                eyebrow="Track Request"
                chapter="Service · 06"
                title="Track your request."
                subtitle="Enter your Request ID (e.g., MWK-2026-100234) to view a live timeline of its progress."
            />
            <section className="bg-white py-16 md:py-20">
                <div className="container-x">
                    <form onSubmit={onSearch} className="mx-auto flex max-w-2xl items-center gap-3 rounded-full border border-ink/10 bg-white p-2 pl-5 shadow-sm" data-testid="track-form">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input
                            className="flex-1 bg-transparent py-3 text-[15px] outline-none placeholder:text-slate-400"
                            placeholder="MWK-2026-100234"
                            value={id}
                            onChange={(e) => setId(e.target.value)}
                            data-testid="track-input"
                        />
                        <button className="btn-primary" data-testid="track-submit">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
                        </button>
                    </form>

                    <AnimatePresence>
                        {result && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.5 }}
                                className="mx-auto mt-16 max-w-3xl overflow-hidden rounded-[24px] border border-ink/10 bg-white"
                                data-testid="track-result"
                            >
                                <div className="flex flex-col justify-between gap-6 border-b border-ink/10 p-8 md:flex-row md:items-end md:p-10">
                                    <div>
                                        <div className="eyebrow mb-2">Request</div>
                                        <div className="font-display text-[26px] font-semibold text-ink">{result.requestId}</div>
                                        <div className="mt-2 text-[13px] text-slate-500">Submitted on {result.submittedOn}</div>
                                    </div>
                                    <div className="rounded-full bg-pink/10 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.24em] text-pink">
                                        Status · Under Review
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-8 p-8 md:grid-cols-3 md:p-10">
                                    <div>
                                        <div className="eyebrow mb-2">Purpose</div>
                                        <div className="text-[14px] text-slate-700">{result.purpose}</div>
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">Requested by</div>
                                        <div className="text-[14px] text-slate-700">{result.name}</div>
                                    </div>
                                    <div>
                                        <div className="eyebrow mb-2">Assigned</div>
                                        <div className="text-[14px] text-slate-700">{result.assignedTo}</div>
                                    </div>
                                </div>
                                {/* Timeline */}
                                <div className="border-t border-ink/10 p-8 md:p-10">
                                    <div className="eyebrow mb-6">Timeline</div>
                                    <ol className="relative border-l border-ink/10 pl-8">
                                        {TIMELINE_STEPS.map((s, i) => {
                                            const done = i <= result.currentStep;
                                            const current = i === result.currentStep;
                                            return (
                                                <motion.li
                                                    key={s.key}
                                                    initial={{ opacity: 0, x: -8 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: i * 0.12, duration: 0.5 }}
                                                    className="relative pb-8 last:pb-0"
                                                    data-testid={`timeline-step-${s.key}`}
                                                >
                                                    <span
                                                        className={`absolute -left-[41px] top-0 grid h-6 w-6 place-items-center rounded-full border ${
                                                            done ? "border-pink bg-pink text-white" : "border-ink/20 bg-white text-slate-400"
                                                        }`}
                                                    >
                                                        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />}
                                                    </span>
                                                    <div className="font-display text-[17px] font-semibold text-ink">{s.label}</div>
                                                    <div className="text-[12px] uppercase tracking-[0.24em] text-slate-500">
                                                        {current ? "In progress" : done ? "Completed" : "Pending"}
                                                    </div>
                                                </motion.li>
                                            );
                                        })}
                                    </ol>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {!result && !loading && (
                        <div className="mx-auto mt-12 max-w-xl text-center text-[13px] text-slate-500">
                            Try <span className="kbd">MWK-2026-100234</span> to see a demo result.
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
